import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './index';
import { firebaseConfig } from './config';

const provider = new GoogleAuthProvider();

const getDeviceInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    online: navigator.onLine,
    screen: `${window.screen.width}x${window.screen.height}`,
  };
};

const logAuthEvent = async (userId, eventType, errorMessage = null, userEmail = null) => {
  try {
    await addDoc(collection(db, 'authEvents'), {
      timestamp: serverTimestamp(),
      userId: userId || null,
      userEmail: userEmail || null,
      eventType,
      errorMessage,
      deviceInfo: getDeviceInfo(),
    });
  } catch (e) {
    console.error('Failed to log auth event:', e);
  }
};

/**
 * ADMIN ONLY: Create a new user via Firebase Auth REST API.
 * Does NOT affect the admin's current session.
 */
export const createUserAsAdmin = async (email, password, displayName, role = 'fieldWorker') => {
  try {
    const response = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${firebaseConfig.apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          returnSecureToken: true,
        }),
      }
    );

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.message);
    }

    const uid = data.localId;

    // Create Firestore user doc with assigned role
    await setDoc(doc(db, 'users', uid), {
      uid,
      email,
      displayName: displayName || email.split('@')[0],
      role,
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || null,
      demoMode: false,
    });

    // Only send password reset for admin/manager roles (they use full passwords)
    // Field workers use PIN — no reset needed
    if (role !== 'fieldWorker') {
      await sendPasswordResetEmail(auth, email);
    }

    await logAuthEvent(uid, 'admin_created_user', null, email);
    return { uid, error: null };
  } catch (error) {
    await logAuthEvent(null, 'admin_create_user_error', error.message, email);
    return { uid: null, error: error.message };
  }
};

export const registerWithEmail = async (email, password, name) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;

    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: name || user.email.split('@')[0],
      role: 'pending',
      createdAt: serverTimestamp(),
      demoMode: false,
    });

    await logAuthEvent(user.uid, 'signup', null, user.email);
    return { user, error: null };
  } catch (error) {
    await logAuthEvent(null, 'signup_error', error.message, email);
    return { user: null, error: error.message };
  }
};

export const loginWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await logAuthEvent(result.user.uid, 'login', null, result.user.email);
    return { user: result.user, error: null };
  } catch (error) {
    await logAuthEvent(null, 'login_error', error.message, email);
    return { user: null, error: error.message };
  }
};

export const loginWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // New Google user — set to PENDING, admin must approve
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email.split('@')[0],
        photoURL: user.photoURL || null,
        role: 'pending',
        createdAt: serverTimestamp(),
        demoMode: false,
      });
      await logAuthEvent(user.uid, 'signup_google_pending', null, user.email);
    } else {
      await logAuthEvent(user.uid, 'login_google', null, user.email);
    }

    return { user, error: null };
  } catch (error) {
    await logAuthEvent(null, 'login_google_error', error.message);
    return { user: null, error: error.message };
  }
};

export const logoutUser = async (uid) => {
  try {
    await logAuthEvent(uid, 'logout', null, auth.currentUser?.email);
    await signOut(auth);
    return { error: null };
  } catch (error) {
    await logAuthEvent(uid, 'logout_error', error.message);
    return { error: error.message };
  }
};

/**
 * Custom password reset via Cloud Function
 * Bypasses Firebase Console templates to use custom domain
 */
export const sendCustomPasswordReset = async (email) => {
  try {
    const url = `https://sendcustompasswordreset-lgrwjptv5a-uc.a.run.app`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to send reset email');
    return { success: true, error: null };
  } catch (error) {
    console.error('Custom password reset error:', error);
    return { success: false, error: error.message };
  }
};

export { onAuthStateChanged, logAuthEvent };
